import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable'; 
import { toast } from 'react-toastify';
import { BASE_URL } from '../../api'; 

// --- 1. IMPROVED DELIVERY PROGRESS BAR ---
const OrderProgressBar = ({ status }) => {
    const steps = ["ORDERED", "PACKED", "SHIPPED", "DELIVERED"];
    let currentStepIndex = steps.indexOf(status);

    if (status === "OUT_FOR_DELIVERY") currentStepIndex = steps.indexOf("SHIPPED");
    if (status === "CANCELED") currentStepIndex = -1; 

    // Calculate width for the green line
    const progressPercent = status === "CANCELED" ? 0 : (currentStepIndex / (steps.length - 1)) * 100;

    return (
        <div className="w-100 mt-4 mb-4 px-2">
            {status === "CANCELED" ? (
                 <div className="alert alert-danger py-1 px-3 d-inline-block small fw-bold">
                    <i className="fa fa-times-circle me-2"></i> ORDER CANCELED
                 </div>
            ) : (
                <div className="position-relative" style={{ margin: '0 10px' }}>
                    {/* Background Line */}
                    <div className="position-absolute top-0 start-0 translate-middle-y w-100 bg-light rounded" 
                         style={{ height: '4px', zIndex: 0, top: '12px' }}></div>
                    
                    {/* Active Green Line */}
                    <div className="position-absolute top-0 start-0 translate-middle-y bg-success rounded transition-all" 
                        style={{ height: '4px', width: `${progressPercent}%`, transition: 'width 0.5s ease', zIndex: 0, top: '12px' }}></div>
                    
                    {/* Steps Container */}
                    <div className="d-flex justify-content-between position-relative" style={{ zIndex: 1 }}>
                        {steps.map((step, index) => {
                            const isCompleted = index <= currentStepIndex;
                            return (
                                <div key={step} className="text-center" style={{ width: '80px' }}>
                                    {/* Circle Icon */}
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center border border-2 mx-auto ${isCompleted ? 'bg-success border-success text-white' : 'bg-white border-secondary text-secondary'}`}
                                        style={{ width: '24px', height: '24px', fontSize: '12px', backgroundColor: '#fff' }}>
                                        {isCompleted && <i className="fa fa-check"></i>}
                                    </div>
                                    {/* Text Label (No absolute positioning to prevent overlap) */}
                                    <div className={`mt-2 small fw-bold ${isCompleted ? 'text-success' : 'text-muted'}`} style={{ fontSize: '10px', lineHeight: '1.2' }}>
                                        {step}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- 2. IMPROVED RETURN PROGRESS BAR ---
const ReturnProgressBar = ({ status }) => {
    const steps = [
        "RETURN_REQUEST_INITIATED", 
        "PRODUCT_RETURNED_SUCCESSFULLY", 
        "REFUND_INITIATED", 
        "REFUNDED_SUCCESSFULLY"
    ];
    
    const stepLabels = {
        "RETURN_REQUEST_INITIATED": "Return Requested",
        "PRODUCT_RETURNED_SUCCESSFULLY": "Returned",
        "REFUND_INITIATED": "Refund Started",
        "REFUNDED_SUCCESSFULLY": "Refunded"
    };

    let currentStepIndex = steps.indexOf(status);
    const progressPercent = (currentStepIndex / (steps.length - 1)) * 100;

    return (
        <div className="w-100 mt-3 mb-4 p-3 bg-light rounded border border-warning">
            <h6 className="text-warning fw-bold small mb-4"><i className="fa fa-undo me-1"></i> RETURN STATUS</h6>
            
            <div className="position-relative" style={{ margin: '0 10px' }}>
                {/* Background Line */}
                <div className="position-absolute top-0 start-0 translate-middle-y w-100 bg-white rounded" 
                     style={{ height: '4px', zIndex: 0, top: '12px' }}></div>
                
                {/* Active Yellow Line */}
                <div className="position-absolute top-0 start-0 translate-middle-y bg-warning rounded transition-all" 
                    style={{ height: '4px', width: `${progressPercent}%`, transition: 'width 0.5s ease', zIndex: 0, top: '12px' }}></div>
                
                {/* Steps */}
                <div className="d-flex justify-content-between position-relative" style={{ zIndex: 1 }}>
                    {steps.map((step, index) => {
                        const isCompleted = index <= currentStepIndex;
                        return (
                            <div key={step} className="text-center" style={{ width: '80px' }}>
                                <div className={`rounded-circle d-flex align-items-center justify-content-center border border-2 mx-auto ${isCompleted ? 'bg-warning border-warning text-white' : 'bg-white border-secondary text-secondary'}`}
                                    style={{ width: '24px', height: '24px', fontSize: '12px', backgroundColor: '#fff' }}>
                                    {isCompleted && <i className="fa fa-check"></i>}
                                </div>
                                <div className={`mt-2 small fw-bold ${isCompleted ? 'text-dark' : 'text-muted'}`} style={{ fontSize: '10px', lineHeight: '1.2' }}>
                                    {stepLabels[step]}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const OrderHistory = () => {
    const [orders, setOrders] = useState([]);
    const [returnRequests, setReturnRequests] = useState({});
    const [loading, setLoading] = useState(true);

    // Modal State
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [formData, setFormData] = useState({
        returnReason: 'Damaged',
        comment: '',
        accountNumber: '',
        accountHolderName: '',
        bankName: '',
        ifscCode: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const userId = localStorage.getItem("userId");
            if(!userId) {
                setLoading(false);
                return;
            }

            const orderResponse = await axios.get(`${BASE_URL}/orders/summary?userId=${userId}`);
            setOrders(orderResponse.data);

            try {
                const returnResponse = await axios.get(`${BASE_URL}/api/return-requests/user/${userId}`);
                const returnsMap = {};
                returnResponse.data.forEach(req => {
                    if(req.order && req.order.serialNo) {
                        returnsMap[req.order.serialNo] = req;
                    }
                });
                setReturnRequests(returnsMap);
            } catch (err) {
                console.warn("Could not fetch return requests", err);
            }

        } catch(err) {
            console.log("Error fetching data", err);
        } finally {
            setLoading(false);
        }
    }

    const handleReturnClick = (orderItem) => {
        setSelectedOrder(orderItem);
        setShowReturnModal(true);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const submitReturnRequest = async () => {
        if (!formData.accountNumber || !formData.ifscCode || !formData.bankName) {
            toast.error("Please fill in all bank details for the refund.");
            return;
        }

        try {
            const payload = {
                orderId: selectedOrder.serialNo,
                ...formData
            };
            
            await axios.post(`${BASE_URL}/api/return-requests/create`, payload);
            
            toast.success("Request submitted. Our team will get back to you shortly.");
            setShowReturnModal(false);
            fetchData(); 
        } catch (error) {
            console.error("Return failed", error);
            toast.error("Failed to submit return request. Please try again.");
        }
    };

    // --- 3. PROFESSIONAL INVOICE GENERATOR ---
    const downloadInvoice = (bill) => {
        const doc = new jsPDF();
        const logoUrl = "/assets/images/header_logo.png";
        
        // Helper to extract user details safely
        // Assuming bill.orders[0] has address info embedded as a string, or you might need to fetch user profile
        // For now, we try to parse the address string from the first order if available
        let customerName = "Valued Customer";
        let customerMobile = "N/A";
        let customerAddress = "";

        if(bill.orders && bill.orders.length > 0) {
            const firstOrder = bill.orders[0];
            if(firstOrder.orderAddress) {
                // Assuming address format: ID@Name@Mobile@House...
                const parts = firstOrder.orderAddress.split('@');
                if(parts.length >= 3) {
                    customerName = parts[1];
                    customerMobile = parts[2];
                    // Reconstruct address for display
                    customerAddress = `${parts[3]}, ${parts[4]}, ${parts[6]} - ${parts[8]}`; 
                }
            }
        }

        const img = new Image();
        img.src = logoUrl;
        
        img.onload = () => {
             // 1. Header & Logo
             doc.addImage(img, 'PNG', 14, 10, 40, 12); // Adjust Logo size/pos
             
             // Company Info (Right Aligned or Centered)
             doc.setFontSize(14); 
             doc.setFont("helvetica", "bold");
             doc.text("Radha Nikunj Bihari Industries", 195, 20, { align: 'right' });
             
             doc.setFontSize(10);
             doc.setFont("helvetica", "normal");
             doc.text("Email: info@divinedepot.in", 195, 26, { align: 'right' });
             doc.text("Call between 9am - 6pm", 195, 31, { align: 'right' });
             doc.text("Ph: +91-6283013500", 195, 36, { align: 'right' });

             // Divider
             doc.setLineWidth(0.5);
             doc.line(14, 42, 196, 42);

             // 2. Bill Details Section
             doc.setFontSize(11);
             doc.setFont("helvetica", "bold");
             doc.text("INVOICE DETAILS", 14, 50);
             
             doc.setFontSize(10);
             doc.setFont("helvetica", "normal");
             doc.text(`Invoice No: INV-${bill.billNo}`, 14, 56);
             doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 61);
             
             // Estimated Delivery (Highlighted)
             doc.setFont("helvetica", "bold");
             doc.setTextColor(0, 128, 0); // Green color
             doc.text("ESTIMATED DELIVERY: 7–10 Days", 14, 68);
             doc.setTextColor(0, 0, 0); // Reset color

             // Customer Details (Right Side)
             doc.setFont("helvetica", "bold");
             doc.text("BILL TO:", 120, 50);
             doc.setFont("helvetica", "normal");
             doc.text(`Name: ${customerName}`, 120, 56);
             doc.text(`Mobile: ${customerMobile}`, 120, 61);
             if(customerAddress) {
                 const splitAddr = doc.splitTextToSize(`Address: ${customerAddress}`, 75);
                 doc.text(splitAddr, 120, 66);
             }

             // 3. Product Table
             const tableColumn = ["#", "Product Name", "Qty", "Price (incl. Shipping)", "Total"];
             const tableRows = [];
             
             let subtotal = 0;

             bill.orders.forEach((order, index) => {
                 const totalItemPrice = order.productRateInDecimal; // This includes shipping per logic
                 const unitPrice = totalItemPrice / parseInt(order.orderQuantity);
                 
                 subtotal += totalItemPrice;

                 tableRows.push([
                     index + 1, 
                     order.productName, 
                     order.orderQuantity, 
                     `Rs. ${unitPrice.toFixed(2)}`, 
                     `Rs. ${totalItemPrice.toFixed(2)}`
                 ]);
             });

             autoTable(doc, { 
                 head: [tableColumn], 
                 body: tableRows, 
                 startY: 85,
                 theme: 'striped',
                 headStyles: { fillColor: [255, 195, 91], textColor: [0, 0, 0] }, // Yellow Header
                 styles: { fontSize: 9, cellPadding: 2 }
             });

             // 4. Summary Footer
             const finalY = doc.lastAutoTable.finalY + 10;
             
             doc.setLineWidth(0.2);
             doc.line(130, finalY, 196, finalY); // Line above total

             doc.setFont("helvetica", "bold");
             doc.text("Grand Total:", 150, finalY + 6, { align: 'right' });
             doc.text(`Rs. ${bill.orderTotalAmount.toFixed(2)}`, 195, finalY + 6, { align: 'right' });

             doc.line(130, finalY + 10, 196, finalY + 10); // Line below total

             // Footer Note
             doc.setFontSize(8);
             doc.setFont("helvetica", "italic");
             doc.text("Thank you for shopping with Divine Depot!", 105, 280, { align: 'center' });

             doc.save(`Invoice_INV-${bill.billNo}.pdf`);
        };
        
        img.onerror = () => { toast.error("Could not load invoice logo."); };
    };

    if (loading) return <div className="text-center py-5"><h3>Loading orders...</h3></div>;

    return (
        <div className="container py-5" style={{ minHeight: '80vh' }}>
            <h2 className="fw-bold mb-4 ps-2 border-start border-4 border-warning">My Orders</h2>

            {orders.length === 0 ? (
                <div className="text-center py-5 bg-light rounded-4">
                    <i className="fa fa-shopping-bag text-muted mb-3" style={{ fontSize: '50px' }}></i>
                    <h4>No orders found</h4>
                </div>
            ) : (
                <div className="row">
                    {orders.map((bill, index) => (
                        <div key={index} className="col-12 mb-4">
                            <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                                <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between">
                                    <span className="fw-bold">Order #{bill.billNo}</span>
                                    <span className="fw-bold">₹{bill.orderTotalAmount.toFixed(2)}</span>
                                </div>

                                <div className="card-body p-4">
                                    {bill.orders.map((item, idx) => {
                                        const returnReq = returnRequests[item.serialNo];
                                        
                                        return (
                                        <div key={idx} className="mb-3 pb-3 border-bottom">
                                            <div className="row align-items-center">
                                                <div className="col-md-2">
                                                    <img src={bill.orderImage && bill.orderImage !== "photo.jpg" ? bill.orderImage : "https://via.placeholder.com/80"} 
                                                        alt={item.productName} className="img-fluid rounded" style={{maxHeight: '80px'}} />
                                                </div>
                                                <div className="col-md-6">
                                                    <h6 className="fw-bold mb-1">{item.productName}</h6>
                                                    <small className="text-muted">Qty: {item.orderQuantity}</small>
                                                </div>
                                                <div className="col-md-4 text-md-end">
                                                    <span className="fw-bold">₹{item.productRateInDecimal.toFixed(2)}</span>
                                                </div>
                                            </div>

                                            {/* Progress Bar Section */}
                                            <div className="mt-3">
                                                {returnReq ? (
                                                    <ReturnProgressBar status={returnReq.status} />
                                                ) : (
                                                    <OrderProgressBar status={item.orderStatus || "ORDERED"} />
                                                )}
                                            </div>

                                            {/* Return Button Logic */}
                                            {!returnReq && item.orderStatus === "DELIVERED" && (
                                                <div className="d-flex justify-content-end mt-2">
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleReturnClick(item)}>
                                                        <i className="fa fa-undo me-1"></i> Return Product
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )})}

                                    <div className="d-flex justify-content-end mt-4">
                                        <button className="btn btn-outline-dark rounded-pill px-4" onClick={() => downloadInvoice(bill)}>
                                            <i className="fa fa-download me-2"></i> Download Invoice
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* --- RETURN REQUEST MODAL --- */}
            {showReturnModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">Return Product</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowReturnModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">Returning: <strong>{selectedOrder?.productName}</strong></p>
                                
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Reason for Return</label>
                                        <select className="form-select" name="returnReason" value={formData.returnReason} onChange={handleFormChange}>
                                            <option value="Damaged">Damaged Product</option>
                                            <option value="Defective">Defective / Not Working</option>
                                            <option value="Didn't like it">Didn't like it</option>
                                            <option value="Wrong product">Wrong product delivered</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Comments (Optional)</label>
                                        <input type="text" className="form-control" name="comment" value={formData.comment} onChange={handleFormChange} />
                                    </div>

                                    {/* Bank Details Section */}
                                    <h6 className="mt-4 mb-2 text-primary fw-bold">Bank Details for Refund</h6>
                                    <div className="col-md-6">
                                        <label className="form-label">Account Number</label>
                                        <input type="text" className="form-control" name="accountNumber" value={formData.accountNumber} onChange={handleFormChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Account Holder Name</label>
                                        <input type="text" className="form-control" name="accountHolderName" value={formData.accountHolderName} onChange={handleFormChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Bank Name</label>
                                        <input type="text" className="form-control" name="bankName" value={formData.bankName} onChange={handleFormChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">IFSC Code</label>
                                        <input type="text" className="form-control" name="ifscCode" value={formData.ifscCode} onChange={handleFormChange} required />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowReturnModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-danger" onClick={submitReturnRequest}>Submit Return Request</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderHistory;